import {
  CAPA_ACTION_STATUS_LABEL_PT_BR,
  CapaActionStatus,
  Permission,
  type SignableAct,
  type SignedCommand,
} from '@qualicloud/contracts';
import { FOLLOW_UP_OFFSET_DAYS } from '@qualicloud/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { SignatureDialog } from '@/components/signature/signature-dialog';
import { SignatureManifest } from '@/components/signature/signature-manifest';
import {
  EffectivenessDialog,
  type EffectivenessDecision,
} from '@/components/sgq/effectiveness-dialog';
import { ExecutionDialog } from '@/components/sgq/execution-dialog';
import { CapaActionStatusBadge, CapaDueBadge } from '@/components/sgq/sgq-badges';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Combobox,
  Dialog,
  EmptyState,
  Field,
  IconAlertOctagon,
  IconAlertTriangle,
  IconInfo,
  IconCalendar,
  IconCheckCircle,
  IconClipboardCheck,
  IconHistory,
  LoadingRegion,
  Skeleton,
  Tabs,
  Textarea,
  cn,
  useToast,
} from '@/design-system';
import {
  capaApi,
  queryKeys,
  sgqSignedActInvalidations,
  toUserMessage,
} from '@/lib/api';
import type { CapaActionDetail } from '@/lib/api/sgq.types';
import { formatDateOnly, formatRegulatedDateTime, truncateHash } from '@/lib/format/datetime';
import { signedActRecordId } from '@/lib/signed-act-record';
import { useDirectory } from '@/lib/use-directory';
import { useAuth, useOrganizationTimeZone } from '@/providers/auth-provider';
import { PageBody, PageHeader } from '@/shell/app-shell';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AÇÃO CORRETIVA 5W2H — execução, reatribuição e verificação de eficácia
 * sgq.md §C.4 e §C.5
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * O AVISO ANTES DE REPROVAR É O CENTRO DESTA TELA.
 *
 * Reprovar a eficácia não é só marcar uma flag: o servidor cria, na MESMA
 * transação, uma SACAP de follow-up para D+3 às 09:00 no fuso da organização,
 * herda as NCs e os participantes da reunião de origem, MOVE esta ação para a
 * nova reunião e a reabre. No legado tudo isso acontecia em silêncio — e dois
 * dos sete passos falhavam sempre, sem ninguém saber: a SACAP de follow-up
 * nascia sem tipo (BUG-REU-02) e SEM PARTICIPANTES (BUG-REU-03).
 *
 * Aqui a consequência é exibida ANTES da confirmação, pelo
 * `<EffectivenessDialog>`, com a data e a hora reais calculadas por
 * `computeFollowUpWindow` — a mesma função de `@qualicloud/domain` que o
 * servidor usa. Não é uma estimativa da tela: é a regra.
 */

/** A decisão sai do `<EffectivenessDialog>` e espera a assinatura. */
type PendingAct = EffectivenessDecision;

export function CapaActionDetailPage() {
  const { actionId } = useParams({ strict: false }) as { actionId: string };
  const [tab, setTab] = useState('plano');
  const [pendingAct, setPendingAct] = useState<PendingAct | null>(null);
  const [executionOpen, setExecutionOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { can, user } = useAuth();
  const timeZone = useOrganizationTimeZone();
  const directory = useDirectory();

  const actionQuery = useQuery({
    queryKey: queryKeys.capa.action(actionId),
    queryFn: () => capaApi.action(actionId),
  });

  const action = actionQuery.data;

  const invalidate = async () => {
    await Promise.all(
      sgqSignedActInvalidations().map((key) => queryClient.invalidateQueries({ queryKey: key })),
    );
    await queryClient.invalidateQueries({ queryKey: queryKeys.capa.action(actionId) });
  };

  const recordExecution = useMutation({
    mutationFn: (params: {
      readonly note: string;
      readonly transition: 'START' | 'COMPLETE' | null;
      readonly progressPercent?: number;
    }) =>
      capaApi.recordExecution(actionId, {
        note: params.note,
        transition: params.transition,
        ...(params.progressPercent === undefined
          ? {}
          : { progressPercent: params.progressPercent }),
        evidenceUploadIds: [],
      }),
    onSuccess: () => {
      setExecutionOpen(false);
      /*
       * Levar para onde o registro caiu.
       *
       * O diálogo fechava sobre a aba "Plano 5W2H" e o texto recém-escrito ia
       * para "Execuções", atrás de um clique: o usuário via um aviso verde e
       * nenhuma prova. Num diário de bordo que não pode ser apagado, ver o que
       * ficou gravado é parte do ato de gravar.
       */
      setTab('execucoes');
      void invalidate();
      toast({
        tone: 'success',
        title: 'Execução registrada',
        description: 'O registro entrou no diário de bordo da ação e não pode mais ser apagado.',
      });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível registrar a execução',
        description: toUserMessage(error),
      }),
  });

  const reassign = useMutation({
    mutationFn: (params: { readonly newAssigneeUserId: string; readonly reason: string }) =>
      capaApi.reassign(actionId, params),
    onSuccess: () => {
      setReassignOpen(false);
      void invalidate();
      toast({
        tone: 'success',
        title: 'Ação redesignada',
        description:
          'A troca virou linha no diário de bordo e evento na trilha de auditoria, e o novo responsável foi notificado.',
      });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível redesignar a ação',
        description: toUserMessage(error),
      }),
  });

  const verify = useMutation({
    mutationFn: (params: { readonly command: SignedCommand; readonly act: PendingAct }) =>
      capaApi.verifyEffectiveness(actionId, {
        ...params.command,
        approved: params.act.approved,
        justification: params.act.justification || null,
        evidenceSummary: params.act.evidenceSummary || null,
        ...(action?.recordHash ? { expectedRecordHash: action.recordHash } : {}),
      }),
  });

  if (actionQuery.isPending) {
    return (
      <LoadingRegion loading label="Carregando a ação corretiva…">
        <div className="p-5">
          <Skeleton className="h-8 w-80" />
          <Skeleton className="mt-3 h-4 w-96" />
          <Skeleton className="mt-6 h-72 w-full" />
        </div>
      </LoadingRegion>
    );
  }

  if (!action) {
    return (
      <PageBody>
        <Card>
          <EmptyState
            icon={<IconClipboardCheck size={22} />}
            title="Ação não encontrada"
            description="A ação não existe ou você não tem permissão para vê-la. Registros de outras organizações são sempre tratados como inexistentes."
            action={
              <Button asChild variant="primary">
                <Link to="/acoes-corretivas">Voltar ao plano de ação</Link>
              </Button>
            }
          />
        </Card>
      </PageBody>
    );
  }

  const isAssignee = action.assigneeUserId === user?.id;

  /*
   * A AÇÃO ESTÁ ABERTA — é o que decide se cabe registro no diário de bordo.
   *
   * Concluída, aguardando verificação ou cancelada: o diário fecha junto, e o
   * que existe a partir daí é a verificação de eficácia.
   */
  const actionIsOpen =
    action.status === CapaActionStatus.PLANNED ||
    action.status === CapaActionStatus.IN_PROGRESS;

  /*
   * REGISTRAR EXECUÇÃO NÃO É PRIVILÉGIO DO RESPONSÁVEL — só a TRANSIÇÃO é.
   *
   * O servidor sempre foi assim: a guarda em `recordExecution` é
   * `if (input.transition && action.assigneeUserId !== principal.userId)`. Ou
   * seja, anotar andamento no diário de bordo é de quem enxerga a ação; iniciar
   * e concluir é de quem a recebeu (segregação de funções, ISO 13485 §8.5.2).
   *
   * A tela era MAIS restritiva que a regra: escondia o botão inteiro de quem
   * não fosse o responsável, e com isso ninguém mais conseguia registrar nada —
   * nem o gerente que acompanha, nem o colega que ajudou na execução. O diário
   * de bordo de uma ação corretiva existe justamente para receber essas vozes.
   */
  const canRecordExecution = actionIsOpen;
  const canTransition = isAssignee && actionIsOpen;

  const canVerify =
    can(Permission.TASK_VERIFY) &&
    action.status === CapaActionStatus.AWAITING_VERIFICATION &&
    action.eligibleVerifierUserIds.includes(user?.id ?? '');

  /*
   * Quem responde é o servidor (`viewerCanReassign`), e não uma dedução daqui.
   * A regra — gerente do departamento da ação ou solicitante — depende de
   * chefia, que mora no IDENTITY e não vem no token.
   */
  const canReassign = action.viewerCanReassign;

  const signedAct: SignableAct = pendingAct?.approved
    ? 'EFFECTIVENESS_VERIFY'
    : 'EFFECTIVENESS_REJECT';

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Plano de ação', to: '/acoes-corretivas' }, { label: action.title }]}
        title={action.title}
        description={`Ação ${action.preventive ? 'preventiva' : 'corretiva'} para a causa-raiz: ${action.rootCauseDescription}`}
        actions={
          <>
            {canRecordExecution ? (
              <Button
                variant="primary"
                loading={recordExecution.isPending}
                onClick={() => setExecutionOpen(true)}
              >
                Registrar execução
              </Button>
            ) : null}
            {canVerify ? (
              <Button
                variant="primary"
                leadingIcon={<IconCheckCircle size={15} />}
                onClick={() => setVerificationOpen(true)}
              >
                Verificar eficácia
              </Button>
            ) : null}
            {canReassign ? (
              <Button variant="secondary" onClick={() => setReassignOpen(true)}>
                Redesignar responsável
              </Button>
            ) : null}
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <CapaActionStatusBadge status={action.status} />
          <CapaDueBadge
            variant="chip"
            state={action.dueState}
            status={action.status}
            dueDate={action.plan.when}
          />
          <Badge tone="outline" srPrefix="Tipo de ação:">
            {action.preventive ? 'Preventiva' : 'Corretiva'}
          </Badge>
          <Badge tone="outline" srPrefix="Progresso:">
            {action.progressPercent}% concluído
          </Badge>
          <Button asChild variant="ghost" size="sm">
            <Link
              to="/nao-conformidades/$nonconformityId"
              /*
               * Só chega aqui quando há NC de origem — a solicitação AVULSA não
               * tem, e o bloco inteiro fica fora nesse caso. O `?? ''` é para o
               * compilador, não para a tela.
               */
              params={{ nonconformityId: action.nonconformityId ?? '' }}
            >
              Abrir a não-conformidade
            </Link>
          </Button>
        </div>
      </PageHeader>

      <PageBody className="flex flex-col gap-6">
        {/*
          Por que o botão de execução NÃO inicia nem conclui — em vez de uma
          tela muda.

          Quem não é o responsável PODE anotar andamento no diário de bordo: é o
          que o servidor sempre permitiu e o que a tela negava. O que continua
          sendo só do responsável é a TRANSIÇÃO — iniciar e concluir —, porque é
          ela que o diário prova depois, e é controle de segregação de funções.

          Sem este cartão a diferença ficaria escondida dentro do diálogo, em
          duas opções desabilitadas sem motivo aparente. Aqui ela é dita antes
          de o usuário tentar, com o nome de quem executa e o caminho de quem
          precisa mesmo assumir a ação.
        */}
        {actionIsOpen && !isAssignee ? (
          <Card className="border-l-4 border-l-info">
            <div className="flex items-start gap-3">
              <IconInfo
                size={20}
                aria-hidden="true"
                className="mt-0.5 shrink-0 text-info-content"
              />
              <div>
                <h2 className="text-base font-semibold text-content">
                  Você pode anotar o andamento, mas não iniciar nem concluir
                </h2>
                <p className="mt-2 text-sm text-content-muted">
                  Quem executa esta ação é{' '}
                  <span className="font-medium text-content">{action.assigneeName ?? '—'}</span>. Em{' '}
                  <span className="font-medium text-content">Registrar execução</span> você
                  acrescenta o que observou ao diário de bordo — o registro fica com o seu nome e
                  não pode ser apagado. Iniciar e concluir são de quem recebeu a ação: é a
                  segregação de funções que a auditoria lê depois.
                </p>
                {canReassign ? (
                  <p className="mt-1.5 text-sm text-content-muted">
                    Se a execução tiver de passar para outra pessoa, use{' '}
                    <span className="font-medium text-content">Redesignar responsável</span>, acima:
                    a troca fica na trilha, com autor e motivo.
                  </p>
                ) : (
                  <p className="mt-1.5 text-sm text-content-muted">
                    Para trocar o responsável, fale com o gerente do departamento da ação ou com
                    quem a pediu
                    {action.requesterName ? (
                      <>
                        {' — '}
                        <span className="font-medium text-content">{action.requesterName}</span>
                      </>
                    ) : null}
                    .
                  </p>
                )}
              </div>
            </div>
          </Card>
        ) : null}

        {/* Por que ninguém pode verificar agora — em vez de um botão morto. */}
        {action.status === CapaActionStatus.AWAITING_VERIFICATION &&
        !canVerify &&
        action.verificationBlockers.length > 0 ? (
          <Card className="border-l-4 border-l-warning">
            <div className="flex items-start gap-3">
              <IconAlertTriangle
                size={20}
                aria-hidden="true"
                className="mt-0.5 shrink-0 text-warning-content"
              />
              <div>
                <h2 className="text-base font-semibold text-content">
                  A verificação de eficácia não está disponível para você
                </h2>
                <ul className="mt-2 flex flex-col gap-1.5">
                  {action.verificationBlockers.map((blocker) => (
                    <li key={blocker} className="text-sm text-content-muted">
                      {blocker}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-sm text-content-muted">
                  Quem verifica a eficácia é o SOLICITANTE da ação —{' '}
                  <span className="font-medium text-content">{action.requesterName ?? '—'}</span>.
                  Quem pediu a ação é quem sabe se o problema dele acabou.
                </p>
              </div>
            </div>
          </Card>
        ) : null}

        {action.status === CapaActionStatus.INEFFECTIVE ? (
          <Card className="border-l-4 border-l-danger">
            <div className="flex items-start gap-3">
              <IconAlertOctagon
                size={20}
                aria-hidden="true"
                className="mt-0.5 shrink-0 text-danger-content"
              />
              <div>
                <h2 className="text-base font-semibold text-content">
                  Eficácia reprovada — ação reaberta
                </h2>
                <p className="mt-1 text-sm text-content-muted">
                  A ação voltou para execução e uma SACAP de follow-up foi criada para reavaliar a
                  causa-raiz e redefinir o plano. Enquanto esta ação não for aprovada, a
                  não-conformidade não pode ser encerrada.
                </p>
                {action.effectivenessChecks.find((check) => check.followUpMeetingId) ? (
                  <div className="mt-3">
                    <Button asChild variant="secondary" leadingIcon={<IconCalendar size={15} />}>
                      <Link
                        to="/reunioes/$meetingId"
                        params={{
                          meetingId: action.effectivenessChecks.find(
                            (check) => check.followUpMeetingId,
                          )?.followUpMeetingId as string,
                        }}
                      >
                        Abrir a SACAP de follow-up
                      </Link>
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          </Card>
        ) : null}

        <Tabs
          ariaLabel="Seções da ação corretiva"
          value={tab}
          onValueChange={setTab}
          tabs={[
            { value: 'plano', label: 'Plano 5W2H', content: <PlanTab action={action} /> },
            {
              value: 'execucoes',
              label: 'Execuções',
              count: action.executions.length,
              content: <ExecutionsTab action={action} timeZone={timeZone} />,
            },
            {
              value: 'eficacia',
              label: 'Eficácia',
              count: action.effectivenessChecks.length,
              content: <EffectivenessTab action={action} timeZone={timeZone} />,
            },
          ]}
        />

        <SignatureManifest
          entries={[...action.signatures]}
          recordLabelFallback={action.title}
          headingLevel={2}
        />
      </PageBody>

      {/* ── Registro de execução ────────────────────────────────────────── */}
      <ExecutionDialog
        open={executionOpen}
        onOpenChange={setExecutionOpen}
        action={action}
        canTransition={canTransition}
        busy={recordExecution.isPending}
        onConfirm={(params) => recordExecution.mutate(params)}
      />

      {/* ── Reatribuição ────────────────────────────────────────────────── */}
      <ReassignDialog
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        busy={reassign.isPending}
        currentAssigneeUserId={action.assigneeUserId}
        /*
         * SÓ O DEPARTAMENTO DA AÇÃO — guarda 8 de `reassign` no servidor:
         * "O usuário selecionado não pertence ao departamento desta ação."
         *
         * A lista trazia a organização inteira, e o texto do diálogo já
         * prometia "colaborador ativo do mesmo setor": escolher qualquer outro
         * dava 422 depois do motivo escrito. Filtrar aqui é o que faz a
         * promessa do diálogo ser verdadeira.
         */
        users={directory.subjects
          .filter((subject) => subject.departmentIds.includes(action.departmentId))
          .map((subject) => ({
            id: subject.userId,
            name: subject.displayName,
            hint: subject.jobTitle,
          }))}
        onConfirm={(params) => reassign.mutate(params)}
      />

      {/* ── Verificação de eficácia ─────────────────────────────────────── */}
      <EffectivenessDialog
        open={verificationOpen}
        onOpenChange={setVerificationOpen}
        timeZone={timeZone}
        assigneeName={action.assigneeName}
        requesterName={action.requesterName}
        onConfirm={(decision) => {
          setVerificationOpen(false);
          setPendingAct(decision);
        }}
      />

      {pendingAct ? (
        <SignatureDialog
          open
          onOpenChange={(next) => {
            if (!next) setPendingAct(null);
          }}
          act={signedAct}
          recordId={
            signedActRecordId(signedAct, {
              documentVersionId: null,
              acknowledgementId: null,
              capaActionId: action.id,
            }) ?? ''
          }
          recordLabel={action.title}
          recordDetails={[
            { label: 'Situação atual', value: CAPA_ACTION_STATUS_LABEL_PT_BR[action.status] },
            { label: 'Responsável pela execução', value: action.assigneeName ?? '—' },
            { label: 'Prazo', value: formatDateOnly(action.plan.when) },
            {
              label: 'Decisão',
              value: pendingAct.approved
                ? 'Resolveu — eficácia aprovada'
                : 'Não resolveu — eficácia reprovada, ação reaberta e SACAP de follow-up criada',
            },
            { label: 'Impressão digital do registro', value: truncateHash(action.recordHash) },
          ]}
          confirmLabel={pendingAct.approved ? 'Aprovar eficácia' : 'Reprovar eficácia'}
          onSign={async (command) => {
            await verify.mutateAsync({ command, act: pendingAct });
          }}
          onSigned={() => {
            const approved = pendingAct.approved;
            setPendingAct(null);
            void invalidate();
            toast({
              tone: approved ? 'success' : 'warning',
              title: approved ? 'Eficácia aprovada' : 'Eficácia reprovada',
              description: approved
                ? 'A ação foi concluída como eficaz. Quando todas as ações da não-conformidade chegarem a este estado, o encerramento fica liberado.'
                : `A ação foi reaberta e uma SACAP de follow-up foi criada para D+${FOLLOW_UP_OFFSET_DAYS}, com as não-conformidades e os participantes da reunião de origem.`,
            });
          }}
        />
      ) : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Abas
// ─────────────────────────────────────────────────────────────────────────────

/** Os sete campos, com o rótulo do 5W2H ao lado do nome em português. */
function PlanTab({ action }: { readonly action: CapaActionDetail }) {
  const fields = [
    { tag: 'What', label: 'O quê', value: action.plan.what },
    { tag: 'Why', label: 'Por quê', value: action.plan.why },
    { tag: 'Where', label: 'Onde', value: action.plan.where },
    { tag: 'When', label: 'Quando', value: formatDateOnly(action.plan.when) },
    { tag: 'Who', label: 'Quem', value: action.plan.whoName ?? action.plan.who },
    { tag: 'How', label: 'Como', value: action.plan.how },
    {
      tag: 'How much',
      label: 'Quanto custa',
      value:
        action.plan.howMuch === null
          ? 'Não informado — nem toda ação corretiva tem custo apurável'
          : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
              action.plan.howMuch,
            ),
    },
  ];

  return (
    <div className="grid gap-5 lg:grid-cols-[2fr_1fr]">
      <Card>
        <CardHeader
          headingLevel={2}
          title="Plano de ação 5W2H"
          description="Os sete campos são estruturados: “Quando” é uma data, comparável com o prazo; “Quem” é um colaborador do sistema, a quem a ação pode ser cobrada e redesignada."
        />
        <dl className="mt-4 flex flex-col gap-3">
          {fields.map((field) => (
            <div key={field.tag} className="rounded-md border border-border bg-surface-sunken p-3">
              <dt className="flex items-baseline gap-2">
                <span className="text-2xs font-semibold uppercase tracking-wide text-content-subtle">
                  {field.tag}
                </span>
                <span className="text-sm font-semibold text-content">{field.label}</span>
              </dt>
              <dd className="mt-1 whitespace-pre-line text-sm text-content">{field.value}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card>
        <CardHeader headingLevel={2} title="Responsabilidades" />
        <dl className="mt-3 grid grid-cols-[9rem_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-content-subtle">Responsável</dt>
          <dd className="text-content">{action.assigneeName ?? '—'}</dd>
          <dt className="text-content-subtle">Solicitante</dt>
          <dd className="text-content">
            {action.requesterName ?? '—'}
            <span className="mt-0.5 block text-xs text-content-subtle">
              Verifica a eficácia quando a ação for concluída.
            </span>
          </dd>
          <dt className="text-content-subtle">Início</dt>
          <dd className="text-content qc-tabular">
            {action.startedAt ? formatDateOnly(action.startedAt) : 'Não iniciada'}
          </dd>
          <dt className="text-content-subtle">Conclusão</dt>
          <dd className="text-content qc-tabular">
            {action.completedAt ? formatDateOnly(action.completedAt) : 'Não concluída'}
          </dd>
        </dl>

        {action.meetingId ? (
          <div className="mt-4">
            <Button asChild variant="secondary" size="sm" leadingIcon={<IconCalendar size={14} />}>
              <Link to="/reunioes/$meetingId" params={{ meetingId: action.meetingId }}>
                Abrir a reunião de origem
              </Link>
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function ExecutionsTab({
  action,
  timeZone,
}: {
  readonly action: CapaActionDetail;
  readonly timeZone: string;
}) {
  if (action.executions.length === 0) {
    return (
      <EmptyState
        icon={<IconHistory size={22} />}
        title="Nenhuma execução registrada"
        description="O diário de bordo guarda cada movimento da ação: início, andamento e conclusão. Só o responsável pela ação registra, e o que entra no diário não pode ser apagado."
      />
    );
  }

  return (
    <ol className="flex flex-col gap-3">
      {[...action.executions]
        .sort((a, b) => b.sequence - a.sequence)
        .map((execution) => (
          <li key={execution.id}>
            <Card
              className={cn(
                execution.transition === 'COMPLETE' && 'border-l-4 border-l-success',
                execution.transition === 'START' && 'border-l-4 border-l-info',
              )}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-content">
                    {execution.recordedByName ?? 'Usuário não identificado'}
                    {execution.transition ? (
                      <Badge
                        tone={execution.transition === 'COMPLETE' ? 'success' : 'info'}
                        size="sm"
                        className="ml-2"
                      >
                        {execution.transition === 'COMPLETE' ? 'Concluiu a ação' : 'Iniciou a ação'}
                      </Badge>
                    ) : null}
                  </p>
                  <p className="mt-0.5 text-xs text-content-subtle qc-tabular">
                    Registro {execution.sequence} ·{' '}
                    {formatRegulatedDateTime(execution.recordedAt, timeZone)}
                  </p>
                </div>
                {execution.progressPercent !== null ? (
                  <Badge tone="outline" srPrefix="Progresso declarado:">
                    {execution.progressPercent}%
                  </Badge>
                ) : null}
              </div>
              <p className="mt-3 whitespace-pre-line text-sm text-content">{execution.note}</p>
            </Card>
          </li>
        ))}
    </ol>
  );
}

function EffectivenessTab({
  action,
  timeZone,
}: {
  readonly action: CapaActionDetail;
  readonly timeZone: string;
}) {
  if (action.effectivenessChecks.length === 0) {
    return (
      <EmptyState
        icon={<IconCheckCircle size={22} />}
        title="Eficácia ainda não verificada"
        description="A verificação acontece depois da conclusão da ação e é ato assinado. Quem verifica é o solicitante, e a autoverificação é bloqueada por padrão: ISO 13485 §8.5.2 exige verificação independente."
      />
    );
  }

  return (
    <ol className="flex flex-col gap-3">
      {action.effectivenessChecks.map((check) => (
        <li key={check.id}>
          <Card
            className={cn(
              'border-l-4',
              check.approved ? 'border-l-success' : 'border-l-danger',
            )}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                {/* h2: na aba de eficácia este é o primeiro cabeçalho depois do h1 da página. */}
                <h2 className="text-base font-semibold text-content">
                  {check.approved ? 'Resolveu — eficácia aprovada' : 'Não resolveu — reprovada'}
                </h2>
                <p className="mt-1 text-sm text-content-muted">
                  Verificada por{' '}
                  <span className="font-medium text-content">
                    {check.verifiedByName ?? 'usuário não identificado'}
                  </span>{' '}
                  em {formatRegulatedDateTime(check.verifiedAt, timeZone)}
                </p>
              </div>
              {check.followUpMeetingId ? (
                <Button asChild variant="secondary" size="sm">
                  <Link
                    to="/reunioes/$meetingId"
                    params={{ meetingId: check.followUpMeetingId }}
                  >
                    Abrir a SACAP de follow-up
                  </Link>
                </Button>
              ) : null}
            </div>

            {check.evidenceSummary ? (
              <div className="mt-3 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
                  Como a eficácia foi comprovada
                </p>
                <p className="mt-1 whitespace-pre-line text-sm text-content">
                  {check.evidenceSummary}
                </p>
              </div>
            ) : null}

            {check.justification ? (
              <div className="mt-3 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
                  Justificativa
                </p>
                <p className="mt-1 whitespace-pre-line text-sm text-content">
                  {check.justification}
                </p>
              </div>
            ) : null}

            <p className="mt-2 text-xs text-content-subtle">
              Assinatura: <span className="font-mono">{truncateHash(check.signatureId, 8, 6)}</span>
            </p>
          </Card>
        </li>
      ))}
    </ol>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Diálogos
// ─────────────────────────────────────────────────────────────────────────────


function ReassignDialog({
  open,
  onOpenChange,
  busy,
  currentAssigneeUserId,
  users,
  onConfirm,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly busy: boolean;
  readonly currentAssigneeUserId: string;
  readonly users: ReadonlyArray<{
    readonly id: string;
    readonly name: string;
    readonly hint?: string | undefined;
  }>;
  readonly onConfirm: (params: {
    readonly newAssigneeUserId: string;
    readonly reason: string;
  }) => void;
}) {
  const [newAssigneeUserId, setNewAssigneeUserId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const fechar = () => {
    /*
     * A19 do laudo dos modais: reabrir o diálogo mostrava "Novo responsável"
     * vazio com o botão HABILITADO — o `value` do combobox tinha sido perdido
     * na remontagem, mas o id continuava no estado, e "Redesignar" mandava um
     * destinatário que a tela não estava mais mostrando.
     */
    setNewAssigneeUserId(null);
    setReason('');
    onOpenChange(false);
  };

  const options = useMemo(
    () =>
      users
        .filter((user) => user.id !== currentAssigneeUserId)
        .map((user) => ({ value: user.id, label: user.name, hint: user.hint })),
    [users, currentAssigneeUserId],
  );

  const invalid = !newAssigneeUserId || reason.trim().length === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && busy) return;
        if (!next) fechar();
      }}
      dismissible={!busy}
      title="Redesignar o responsável"
      description="A ação só pode ser redesignada a um colaborador ativo do mesmo setor, e enquanto ainda não estiver concluída ou cancelada. A troca fica no diário de bordo e na trilha de auditoria."
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={fechar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={invalid}
            onClick={() =>
              onConfirm({
                newAssigneeUserId: newAssigneeUserId as string,
                reason: reason.trim(),
              })
            }
          >
            Redesignar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Novo responsável" required>
          <Combobox
            items={options}
            value={newAssigneeUserId}
            onValueChange={setNewAssigneeUserId}
            placeholder="Buscar colaborador…"
            emptyMessage="Nenhum outro colaborador ativo neste departamento."
          />
        </Field>

        <Field
          label="Motivo da redesignação"
          required
          description="Fica registrado no diário de bordo da ação e na trilha de auditoria."
        >
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={4}
            maxLength={2000}
            showCounter
          />
        </Field>
      </div>
    </Dialog>
  );
}

